if (!customElements.get('cart-discount')) {
  class CartDiscount extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.toggleButton = this.querySelector('.cart-discount__toggle');
      this.form = this.querySelector('.cart-discount__form');
      this.input = this.querySelector('.cart-discount__input');
      this.applyButton = this.querySelector('.cart-discount__btn');
      this.removeButtons = [
        ...this.querySelectorAll('.cart-discount__remove'),
      ];
      this.message = this.querySelector('.cart-discount__message');

      if (
        !this.toggleButton ||
        !this.form ||
        !this.input ||
        !this.applyButton ||
        !this.message
      ) {
        return;
      }

      this.isInitialized = true;
      this.isLoading = false;
      this.appliedCodes = this.getCodesFromElement();

      this.toggleButton.addEventListener('click', () => this.toggleForm());
      this.applyButton.addEventListener('click', () => this.applyDiscount());
      this.removeButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          this.removeDiscount(button.dataset.discountCode || '');
        });
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        this.applyDiscount();
      });
    }

    toggleForm() {
      if (this.isLoading) return;

      const willOpen = this.form.hidden;
      this.form.hidden = !willOpen;
      this.toggleButton.setAttribute('aria-expanded', String(willOpen));

      if (willOpen) {
        this.input.focus();
      } else {
        this.hideMessage();
      }
    }

    async applyDiscount() {
      if (this.isLoading) return;

      const code = this.input.value.trim();
      if (!code) {
        this.showMessage(this.message.dataset.emptyMessage, 'error');
        return;
      }

      const requestedCodes = this.uniqueCodes([...this.appliedCodes, code]);
      this.setLoading(true);

      try {
        const cart = await this.updateDiscount(requestedCodes);
        const appliedCodes = this.getAppliedDiscountCodes(cart);

        if (!this.includesCode(appliedCodes, code)) {
          this.showMessage(this.message.dataset.invalidMessage, 'error');
          return;
        }

        this.renderCart(cart);
      } catch (error) {
        console.error('Discount code could not be applied.', error);
        this.showMessage(this.message.dataset.errorMessage, 'error');
      } finally {
        if (this.isConnected) this.setLoading(false);
      }
    }

    async removeDiscount(codeToRemove) {
      if (this.isLoading || !codeToRemove) return;

      const remainingCodes = this.appliedCodes.filter(
        (code) => this.normalizeCode(code) !== this.normalizeCode(codeToRemove)
      );
      this.setLoading(true);

      try {
        const cart = await this.updateDiscount(remainingCodes);
        const appliedCodes = this.getAppliedDiscountCodes(cart);

        if (this.includesCode(appliedCodes, codeToRemove)) {
          throw new Error('The discount code is still applied.');
        }

        this.renderCart(cart);
      } catch (error) {
        console.error('Discount code could not be removed.', error);
        this.showMessage(this.message.dataset.errorMessage, 'error');
      } finally {
        if (this.isConnected) this.setLoading(false);
      }
    }

    async updateDiscount(codes) {
      const response = await fetch(this.cartUpdateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: codes.join(','),
          sections: ['cart-drawer'],
          sections_url: window.location.pathname,
        }),
      });

      let cart;
      try {
        cart = await response.json();
      } catch (error) {
        throw new Error('Shopify returned an unreadable cart response.');
      }

      if (!response.ok) {
        throw new Error(cart.description || cart.message || 'Cart update failed.');
      }

      return cart;
    }

    get cartUpdateUrl() {
      if (window.routes && window.routes.cart_update_url) {
        return window.routes.cart_update_url;
      }

      const root = window.Shopify?.routes?.root || '/';
      return `${root}cart/update.js`;
    }

    getCodesFromElement() {
      return this.uniqueCodes(
        [...this.querySelectorAll('[data-discount-code]')]
          .map((element) => element.dataset.discountCode || '')
          .filter(Boolean)
      );
    }

    getAppliedDiscountCodes(cart) {
      const codes = [];
      const addCode = (code) => {
        if (!code || this.includesCode(codes, code)) return;
        codes.push(code);
      };

      const applications = [
        ...(cart.cart_level_discount_applications || []),
        ...(cart.discount_applications || []),
      ];

      for (const application of applications) {
        if (application?.type === 'discount_code') {
          addCode(application.title || application.code || '');
        }
      }

      for (const item of cart.items || []) {
        const allocations =
          item.line_level_discount_allocations || item.discount_allocations || [];

        for (const allocation of allocations) {
          const application = allocation.discount_application;
          if (application?.type === 'discount_code') {
            addCode(application.title || application.code || '');
          }
        }
      }

      for (const discountCode of cart.discount_codes || []) {
        if (discountCode?.applicable !== false) {
          addCode(discountCode?.code || '');
        }
      }

      const sectionHtml = cart.sections?.['cart-drawer'];
      if (sectionHtml) {
        const sectionDocument = new DOMParser().parseFromString(
          sectionHtml,
          'text/html'
        );

        sectionDocument
          .querySelectorAll('.cart-discount__applied-tag[data-discount-code]')
          .forEach((element) => addCode(element.dataset.discountCode || ''));
      }

      return codes;
    }

    includesCode(codes, code) {
      const normalizedCode = this.normalizeCode(code);
      return codes.some(
        (candidate) => this.normalizeCode(candidate) === normalizedCode
      );
    }

    uniqueCodes(codes) {
      return codes.reduce((unique, code) => {
        if (code && !this.includesCode(unique, code)) unique.push(code);
        return unique;
      }, []);
    }

    normalizeCode(code) {
      return String(code).trim().toLocaleLowerCase();
    }

    renderCart(cart) {
      const cartDrawer = document.querySelector('cart-drawer');

      if (
        cartDrawer &&
        typeof cartDrawer.renderContents === 'function' &&
        cart.sections?.['cart-drawer']
      ) {
        cartDrawer.renderContents(cart, false);
      } else if (cart.sections?.['cart-drawer']) {
        const sectionDocument = new DOMParser().parseFromString(
          cart.sections['cart-drawer'],
          'text/html'
        );
        const nextBody = sectionDocument.querySelector('#CartDrawer-Body');
        const currentBody = document.querySelector('#CartDrawer-Body');

        if (nextBody && currentBody) {
          currentBody.replaceWith(nextBody);
        }
      }

      if (typeof updateFreeShipping === 'function') {
        updateFreeShipping();
      }

      document.dispatchEvent(
        new CustomEvent('cart:discount:updated', {
          detail: { cart },
        })
      );
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.input.disabled = isLoading;
      this.applyButton.disabled = isLoading;
      this.removeButtons.forEach((button) => {
        button.disabled = isLoading;
      });
      this.toggleButton.setAttribute('aria-busy', String(isLoading));
    }

    showMessage(text, type) {
      this.message.textContent = text;
      this.message.classList.remove(
        'cart-discount__message--success',
        'cart-discount__message--error'
      );
      this.message.classList.add(`cart-discount__message--${type}`);
      this.message.hidden = false;
    }

    hideMessage() {
      this.message.hidden = true;
      this.message.textContent = '';
      this.message.classList.remove(
        'cart-discount__message--success',
        'cart-discount__message--error'
      );
    }
  }

  customElements.define('cart-discount', CartDiscount);
}
